'use strict'

/**
 * Region Selector Popup
 * Handles the display and functionality of the region selection popup based on user location
 */
class RegionSelectorPopup {
  constructor() {
    this.popup = null
    this.backdrop = null
    this.buttons = null
    this.userCountryCache = null // Cache user country to avoid multiple API calls
    this.isVisible = false
    this.preferenceKey = 'region-selector-preference'

    this.init()
  }

  /**
   * Initialize the popup functionality
   */
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup())
    } else {
      this.setup()
    }
  }

  /**
   * Setup popup elements and event listeners
   */
  async setup() {
    this.popup = document.getElementById('region-selector-popup')

    if (!this.popup) {
      console.warn('Region selector popup element not found')
      return
    }

    // Load configuration from section
    this.loadConfiguration()

    this.backdrop = this.popup.querySelector('.region-selector-popup__backdrop')

    // Check if we should show the popup (now async)
    const shouldShow = await this.shouldShowPopup()
    if (shouldShow) {
      this.generateButtons()
      this.bindEvents()
      this.showPopup()
    }
  }

  /**
   * Load configuration from the region selector section
   */
  loadConfiguration() {
    const configScript = document.getElementById('region-selector-config')

    if (configScript) {
      try {
        this.config = JSON.parse(configScript.textContent)
      } catch (error) {
        console.warn('Error parsing region selector configuration:', error)
        this.config = this.getDefaultConfig()
      }
    } else {
      console.warn('Region selector configuration not found, using defaults')
      this.config = this.getDefaultConfig()
    }
  }

  /**
   * Get default configuration if section config is not available
   */
  getDefaultConfig() {
    return {
      heading: 'Select Your Region To Start Shopping',
      buttons: [
        {
          id: 'default-us',
          text: 'Shop US Site',
          subtitle: 'FOR US & CANADA',
          flag: '🇺🇸',
          url: '',
        },
        {
          id: 'default-uk',
          text: 'Shop UK Site',
          subtitle: 'FOR UK & EU',
          flag: '🇬🇧',
          url: '',
        },
      ],
    }
  }

  /**
   * Get user's country from Shopify browsing context API
   * @returns {Promise<string|null>}
   */
  async getUserCountry() {
    // Return cached result if available
    if (this.userCountryCache !== null) {
      // console.log('Region selector: Using cached user country:', this.userCountryCache);
      return this.userCountryCache
    }

    try {
      // console.log('Region selector: Fetching user country from browsing context API...');
      const response = await fetch('/browsing_context_suggestions.json')

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      const country = data?.detected_values?.country?.handle

      // Cache the result
      this.userCountryCache = country || null
      // console.log('Region selector: Detected user country:', country);

      return this.userCountryCache
    } catch (error) {
      console.warn('Error getting user country from browsing context:', error)
      // Cache the null result to avoid repeated failed requests
      this.userCountryCache = null
      return null
    }
  }

  /**
   * Get current site host from Liquid data
   * @returns {string|null}
   */
  getCurrentSiteHost() {
    try {
      const siteData = document.getElementById('shopify-site-data')
      return siteData?.getAttribute('data-site-host') || 'sacheu.com'
    } catch (error) {
      console.warn('Error getting site host:', error)
      return null
    }
  }

  /**
   * Convert site host to region code for compatibility
   * @param {string|null} host - The site host (e.g., 'sacheu.com')
   * @returns {string|null} - Region code ('US' or 'GB')
   */
  getRegionFromHost(host) {
    if (!host) return null

    if (host.includes('sacheu.com')) {
      return 'US'
    }
    if (host.includes('sacheu.co.uk')) {
      return 'GB'
    }

    return null
  }

  /**
   * Check if user was redirected from region selection (has ?redirected=true parameter)
   * @returns {boolean}
   */
  hasRedirectedParameter() {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('redirected') === 'true'
    } catch (error) {
      console.warn('Error checking redirected parameter:', error)
      return false
    }
  }

  /**
   * Store user's region preference in sessionStorage
   * @param {string} selectedRegion - The region the user selected ('US' or 'GB')
   */
  storeRegionPreference(selectedRegion) {
    try {
      if (typeof sessionStorage === 'undefined') {
        console.warn('sessionStorage not available, preference not stored')
        return
      }

      const preference = {
        selected: selectedRegion.toUpperCase(),
        timestamp: Date.now(),
        expires: Date.now() + 24 * 60 * 60 * 1000, // 24 hours fallback
      }

      sessionStorage.setItem(this.preferenceKey, JSON.stringify(preference))
      // console.log('Region preference stored:', preference);
    } catch (error) {
      console.warn('Failed to store region preference:', error)
    }
  }

  /**
   * Get user's stored region preference from sessionStorage
   * @returns {string|null} - Stored region preference or null
   */
  getStoredRegionPreference() {
    try {
      if (typeof sessionStorage === 'undefined') {
        return null
      }

      const stored = sessionStorage.getItem(this.preferenceKey)
      if (!stored) {
        return null
      }

      const preference = JSON.parse(stored)

      // Validate preference structure
      if (!preference.selected || !preference.timestamp) {
        this.clearRegionPreference()
        return null
      }

      // Optional: Check if preference has expired (fallback)
      if (preference.expires && Date.now() > preference.expires) {
        this.clearRegionPreference()
        return null
      }

      // console.log('Retrieved region preference:', preference);
      return preference.selected
    } catch (error) {
      console.warn('Failed to retrieve region preference:', error)
      this.clearRegionPreference() // Clear corrupted data
      return null
    }
  }

  /**
   * Clear stored region preference
   */
  clearRegionPreference() {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(this.preferenceKey)
      }
    } catch (error) {
      console.warn('Failed to clear region preference:', error)
    }
  }

  /**
   * Check if popup should show based on user location vs site region
   * @param {string|null} userCountry - User's country code
   * @param {string|null} siteRegion - Current site's region
   * @returns {boolean}
   */
  shouldShowBasedOnLocation(userCountry, siteRegion) {
    // Show if we can't detect user or site region (fallback to show)
    if (!userCountry || !siteRegion) {
      // console.log('Region selector: Missing location data, showing popup as fallback');
      return false // DEBUG - changed to false for testing
    }

    // Convert to uppercase for consistency
    const user = userCountry.toUpperCase()
    const site = siteRegion.toUpperCase()

    // console.log(`Region selector: User country: ${user}, Site region: ${site}`);

    // Show if user not in US/UK (other countries always see popup)
    if (user !== 'US' && user !== 'GB') {
      // console.log('Region selector: User from other country, showing popup');
      return true
    }

    // Show if US user on UK site (GB)
    if (user === 'US' && site === 'GB') {
      // console.log('Region selector: US user on UK site, showing popup');
      return true
    }

    // Show if UK user (GB) on US site
    if (user === 'GB' && site === 'US') {
      // console.log('Region selector: UK user on US site, showing popup');
      return true
    }

    // Don't show if:
    // - US user on US site
    // - UK user (GB) on UK site (GB)
    // console.log('Region selector: User region matches site region, not showing popup');
    return false
  }

  /**
   * Generate buttons dynamically from configuration
   */
  generateButtons() {
    const headingElement = document.getElementById('region-popup-heading')
    const buttonsContainer = document.getElementById('region-popup-buttons')

    if (!headingElement || !buttonsContainer) {
      console.warn('Region popup elements not found')
      return
    }

    // Update heading
    headingElement.textContent = this.config.heading

    // Clear existing buttons
    buttonsContainer.innerHTML = ''

    // Generate buttons from configuration
    this.config.buttons.forEach((buttonConfig, index) => {
      if (!buttonConfig.text) return

      const buttonGroup = document.createElement('div')
      buttonGroup.className = 'region-selector-popup__button-group'

      const button = document.createElement('button')
      button.type = 'button'
      button.className = `region-selector-popup__button region-selector-popup__button--${index}`
      button.setAttribute('data-region', `region-${index}`)
      button.setAttribute('data-url', buttonConfig.url || '')
      button.setAttribute('aria-label', buttonConfig.text)

      const flag = document.createElement('span')
      flag.className = 'region-selector-popup__button-flag'
      flag.textContent = buttonConfig.flag || '🌍'

      const text = document.createElement('span')
      text.className = 'region-selector-popup__button-text'
      text.textContent = buttonConfig.text

      button.appendChild(flag)
      button.appendChild(text)

      const subtitle = document.createElement('span')
      subtitle.className = 'region-selector-popup__button-subtitle'
      subtitle.textContent = buttonConfig.subtitle || ''

      buttonGroup.appendChild(button)
      if (buttonConfig.subtitle) {
        buttonGroup.appendChild(subtitle)
      }

      buttonsContainer.appendChild(buttonGroup)
    })

    // Update buttons reference for event binding
    this.buttons = this.popup.querySelectorAll('.region-selector-popup__button')
  }

  /**
   * Check if popup should be displayed
   * @returns {Promise<boolean>}
   */
  async shouldShowPopup() {
    try {
      // Don't show in Shopify admin or theme editor
      if (window.Shopify && window.Shopify.designMode) {
        return false
      }

      // Don't show for bots/crawlers
      if (this.isBot()) {
        return false
      }

      // Don't show if user was just redirected from region selection
      if (this.hasRedirectedParameter()) {
        // console.log('Region selector: User was redirected, not showing popup');
        return false
      }

      // Don't show if there are no valid buttons configured
      if (!this.config || !this.config.buttons || this.config.buttons.length === 0) {
        console.warn('Region selector popup: No buttons configured')
        return false
      }

      // Check if at least one button has required fields
      const hasValidButton = this.config.buttons.some((button) => button.text && button.text.trim() !== '')

      if (!hasValidButton) {
        console.warn('Region selector popup: No valid buttons found')
        return false
      }

      // Check stored user preference first
      const storedPreference = this.getStoredRegionPreference()
      const siteHost = this.getCurrentSiteHost()
      const siteRegion = this.getRegionFromHost(siteHost)

      if (storedPreference && siteRegion) {
        // If user has a stored preference and it matches current site, don't show popup
        if (storedPreference === siteRegion.toUpperCase()) {
          // console.log('Region selector: Stored preference matches current site, not showing popup');
          return false
        }
      }

      // Location-based check (only if no matching preference)
      const userCountry = await this.getUserCountry()

      if (!this.shouldShowBasedOnLocation(userCountry, siteRegion)) {
        return false
      }

      return true
    } catch (error) {
      console.warn('Error in shouldShowPopup:', error)
      return false // Fallback to show popup if error - DEBUG - changed to false for testing
    }
  }

  /**
   * Simple bot detection
   * @returns {boolean}
   */
  isBot() {
    const userAgent = navigator.userAgent.toLowerCase()
    const botPatterns = [
      'googlebot',
      'bingbot',
      'slurp',
      'duckduckbot',
      'baiduspider',
      'yandexbot',
      'facebookexternalhit',
      'twitterbot',
      'linkedinbot',
      'pinterest',
      'crawler',
      'spider',
      'bot',
      'headless',
    ]

    return botPatterns.some((pattern) => userAgent.includes(pattern))
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Button click handlers
    this.buttons.forEach((button) => {
      button.addEventListener('click', (e) => this.handleRegionSelection(e))
    })

    // Backdrop click to close (optional - remove if you don't want this)
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.hidePopup())
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hidePopup()
      }
    })
  }

  /**
   * Handle region selection button clicks
   * @param {Event} event
   */
  async handleRegionSelection(event) {
    const button = event.currentTarget
    const region = button.getAttribute('data-region')
    const redirectUrl = button.getAttribute('data-url')

    if (!region) return

    // Determine the target region based on the redirect URL or current site
    const currentSiteHost = this.getCurrentSiteHost()
    let targetRegion = this.getRegionFromHost(currentSiteHost)

    if (redirectUrl && redirectUrl.trim() !== '') {
      // If redirecting, try to determine target region from URL
      try {
        const urlObj = new URL(redirectUrl)
        // Use the helper method to determine target region from redirect URL
        targetRegion = this.getRegionFromHost(urlObj.hostname)
      } catch (error) {
        console.warn('Could not parse redirect URL for region detection:', error)
      }
    }

    // Store the user's preference for the target region
    if (targetRegion) {
      this.storeRegionPreference(targetRegion)
    }

    // Disable all buttons to prevent double-clicks
    this.buttons.forEach((btn) => {
      btn.disabled = true
      btn.style.opacity = '0.6'
    })

    // Handle redirect or stay on current site
    if (redirectUrl && redirectUrl.trim() !== '') {
      await this.redirectToUrl(redirectUrl)
    } else {
      this.hidePopup()
    }
  }

  /**
   * Redirect to specified URL
   * @param {string} url - The URL to redirect to
   */
  async redirectToUrl(url) {
    let finalUrl = url

    // If it's a different domain, preserve the current path
    try {
      const urlObj = new URL(url)
      const currentPath = window.location.pathname + window.location.search

      // If it's a different domain and no path is specified, add current path
      if (urlObj.hostname !== window.location.hostname && urlObj.pathname === '/') {
        finalUrl = `${url.replace(/\/$/, '')}${currentPath}`
      }

      // For cross-domain redirects, check if user is from outside US/UK
      if (urlObj.hostname !== window.location.hostname) {
        const userCountry = await this.getUserCountry()

        // If user is from outside US/UK, append redirected parameter
        if (userCountry && userCountry.toUpperCase() !== 'US' && userCountry.toUpperCase() !== 'GB') {
          const separator = finalUrl.includes('?') ? '&' : '?'
          finalUrl = `${finalUrl}${separator}redirected=true`
        }
      }
    } catch (error) {
      // If URL parsing fails, use as-is
      console.warn('Invalid URL provided:', url)
    }

    // Add a small delay for better UX
    setTimeout(() => {
      window.location.href = finalUrl
    }, 300)
  }

  /**
   * Show the popup with animation
   */
  showPopup() {
    if (this.isVisible || !this.popup) return

    // Prevent body scroll
    document.body.classList.add('region-selector-popup-open')

    // Show popup
    this.popup.style.display = 'block'

    // Force reflow for animation
    this.popup.offsetHeight

    // Add show class for animation
    this.popup.classList.add('show', 'animating')
    this.isVisible = true

    // Remove animating class after animation completes
    setTimeout(() => {
      this.popup.classList.remove('animating')
    }, 300)

    // Focus management for accessibility
    this.popup.setAttribute('aria-hidden', 'false')

    // Focus first button
    const firstButton = this.popup.querySelector('.region-selector-popup__button')
    if (firstButton) {
      firstButton.focus()
    }
  }

  /**
   * Hide the popup with animation
   */
  hidePopup() {
    if (!this.isVisible || !this.popup) return

    // Remove show class for fade out
    this.popup.classList.remove('show')
    this.isVisible = false

    // Remove from DOM after animation
    setTimeout(() => {
      this.popup.style.display = 'none'
      document.body.classList.remove('region-selector-popup-open')
    }, 300)

    // Accessibility
    this.popup.setAttribute('aria-hidden', 'true')
  }
}

// Initialize when script loads
document.addEventListener('DOMContentLoaded', function () {
  new RegionSelectorPopup()
})

// Also initialize immediately if DOM is already ready
if (document.readyState !== 'loading') {
  new RegionSelectorPopup()
}
